import React, { useEffect, useContext } from 'react';
import { X } from 'lucide-react';
import { ThemeContext } from '../../context/ThemeContext';
import styles from './Modal.module.css';

/**
 * Modal component for displaying content in a modal dialog
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.title - Title of the modal
 * @param {React.ReactNode} props.children - Content to display in the modal
 * @param {string} props.className - Additional class name for the modal
 * @param {string} props.backdropClassName - Additional class name for the backdrop
 * @param {boolean} props.closeOnBackdropClick - Whether to close modal when clicking backdrop (default: true)
 */
const Modal = ({ isOpen, onClose, title, children, className = '', backdropClassName = '', closeOnBackdropClick = true }) => {
  const { isDarkMode } = useContext(ThemeContext);
  
  // Add event listener to close modal on escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      // Restore scrolling when modal is closed
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Close modal when clicking outside of content (if enabled)
  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`${styles.modalBackdrop} ${backdropClassName}`} onClick={handleBackdropClick}>
      <div className={`${styles.modalContent} ${className} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
        <div className={styles.modalHeader}>
          {title && <h2 className={styles.modalTitle}>{title}</h2>}
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>
        <div className={styles.modalBody}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;